import { useEffect } from 'react';
import { useSelector } from 'react-redux';

import {
  instrumentSnapshotSubscription,
  marketDataSubscribeTradeSubscription,
} from '../lib/store/market-data/subscriptionHelpers';
import { hasValidCookie } from '../lib/store/selectors';
import InstrumentSnapshotCache from '../lib/store-util/InstrumentSnapshotCache';
import TradePriceCache, { CalculatedTradePriceDataMap } from '../lib/store-util/TradePriceCache';

import { IndexPrice } from '../shared_components/MarketIndexes/MarketIndexes.types';

const MDS_SYMBOL_BY_INDEX: Record<string, string> = {
  '^GSPC': 'SPX',
  '^NDX': 'NDX',
  '^DJI': 'DJX',
  '^RUI': 'RUI',
  '^VIX': 'VIX',
};

const MDS_SYMBOLS = Object.values(MDS_SYMBOL_BY_INDEX).map(symbol => ({ symbol }));

const selectPrices = (data: CalculatedTradePriceDataMap): IndexPrice[] => (
  Object.entries(MDS_SYMBOL_BY_INDEX).flatMap(([symbol, mdsSymbol]) => {
    const livePrice = data[mdsSymbol];
    if (livePrice?.currentPrice == null) return [];

    const percent = livePrice.calculatedSymbolPrices.changedPercentMainPrice;
    let changesPercentage = livePrice.direction === '-' && percent != null ? -percent : percent;

    // Indexes do not trade before the open, so the change is 0 until the first tick.
    // Show the previous session's change from the snapshot instead. The snapshot cache is
    // always updated right before the trade price cache, so reading it here is safe.
    if (changesPercentage === 0) {
      changesPercentage = InstrumentSnapshotCache.get(mdsSymbol)?.prevPercentageChange ?? 0;
    }

    return [{ symbol, price: livePrice.currentPrice, changesPercentage }];
  })
);

const arePricesEqual = (previous: IndexPrice[], next: IndexPrice[]): boolean => (
  previous.length === next.length && previous.every((price, index) => (
    price.symbol === next[index].symbol
    && price.price === next[index].price
    && price.changesPercentage === next[index].changesPercentage
  ))
);

export const useMarketIndexPrices = () => {
  const isAuthenticated = useSelector(hasValidCookie);

  useEffect(() => {
    if (!isAuthenticated) return undefined;

    instrumentSnapshotSubscription(MDS_SYMBOLS);
    marketDataSubscribeTradeSubscription(MDS_SYMBOLS);

    return () => {
      instrumentSnapshotSubscription(MDS_SYMBOLS, true);
      marketDataSubscribeTradeSubscription(MDS_SYMBOLS, true);
    };
  }, [isAuthenticated]);

  return TradePriceCache.useCustom(selectPrices, arePricesEqual);
};
